import { Toolbar, Typography, Popover, Box, Button, IconButton, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Settings as SettingsIcon } from '@material-ui/icons';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { canSeeSettingsTab, isProductEnabled } from '../../../../utils/helpers';
import AdminJobsButton from '@components/AdminJobs/AdminJobsButton';
import HelpMenu from '../HelpMenu';
import Logo from '../Logo';
import NavLink from '../NavLink';
import SettingsMenu from '../SettingsMenu';
import messages from '../messages';

const useStyles = makeStyles(theme => ({
  grow: {
    flexGrow: 1,
    marginBottom: '20px'
  },
  title: {
    display: 'none',
    [theme.breakpoints.up('sm')]: {
      display: 'block'
    },
    marginRight: 10
  },
  sectionDesktop: {
    display: 'none',
    [theme.breakpoints.up('sm')]: {
      display: 'flex'
    }
  },
  sectionMobile: {
    display: 'flex',
    [theme.breakpoints.up('md')]: {
      display: 'none'
    }
  },
  popoverContent: {
    padding: theme.spacing(2),
    maxWidth: '300px'
  }
}));

export function DesktopNavBar({ username, user, handleProfileMenuOpen, hasMessagingAlert }) {
  const classes = useStyles();
  const intl = useIntl();
  const history = useHistory();
  const [anchorElAlert, setAnchorElAlert] = useState(null);

  const handleAlertClick = (event) => {
    setAnchorElAlert(event.currentTarget);
  };

  const handleAlertClose = () => {
    setAnchorElAlert(null);
  };

  const openAlert = Boolean(anchorElAlert);

  return (
    <Toolbar>
      <Typography className={classes.title} variant="h6" noWrap>
        <Logo user={user} />
      </Typography>
      {isProductEnabled('TicketingSystem', user.companyDetails.products) && (
        <>
          <NavLink aria-controls="simple-menu" aria-haspopup="true" link="/tours">
            {intl.formatMessage(messages.tours)}
          </NavLink>
          <NavLink aria-controls="simple-menu" aria-haspopup="true" link="/manifest">
            {intl.formatMessage(messages.manifest)}
          </NavLink>
          <NavLink aria-controls="simple-menu" aria-haspopup="true" link="/bookings">
            {intl.formatMessage(messages.bookings)}
          </NavLink>
          <NavLink aria-controls="simple-menu" aria-haspopup="true" link="/channel-manager">
            {intl.formatMessage(messages.channelManager)}
          </NavLink>
          <NavLink aria-controls="simple-menu" aria-haspopup="true" link="/promotions">
            {intl.formatMessage(messages.promotions)}
          </NavLink>
          {user.role === 'system_admin' && (
            <NavLink aria-controls="simple-menu" aria-haspopup="true" link="/messaging">
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {intl.formatMessage(messages.messaging)}
                {hasMessagingAlert && (
                  <span
                    style={{
                      width: '12px',
                      height: '12px',
                      backgroundColor: '#ef4444',
                      color: 'white',
                      borderRadius: '50%',
                      fontSize: '9px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginLeft: '8px',
                      fontWeight: 'bold',
                      flexShrink: 0,
                      zIndex: 10
                    }}
                    title="Review links missing for some tours"
                  >
                    !
                  </span>
                )}
              </div>
            </NavLink>
          )}
        </>
      )}
      {isProductEnabled('TicketingSystem', user.companyDetails.products) &&
        isProductEnabled('TipDirect', user.companyDetails.products) && (
          <NavLink aria-controls="simple-menu" aria-haspopup="true" link="/tipdirect">
            {intl.formatMessage((user.userFeatures?.tipsDisabled ?? user.tipsDisabled) ? messages.getReviews : messages.tipdirect)}
          </NavLink>
        )}
      <div className={classes.grow} />
      <div className={classes.sectionDesktop} style={{ display: 'flex', alignItems: 'center' }}>
        {hasMessagingAlert && (
          <>
            <button
              type="button"
              onClick={handleAlertClick}
              aria-label="Action required: Review links missing"
              aria-haspopup="true"
              style={{
                border: 0,
                padding: 0,
                fontFamily: 'inherit',
                lineHeight: 'normal',
                width: '18px',
                height: '18px',
                backgroundColor: '#ef4444',
                color: 'white',
                borderRadius: '50%',
                fontSize: '11px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                flexShrink: 0,
                cursor: 'pointer',
                marginRight: '12px'
              }}
              title="Action required: Review links missing"
            >
              !
            </button>
            <Popover
              open={openAlert}
              anchorEl={anchorElAlert}
              onClose={handleAlertClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <Box className={classes.popoverContent}>
                <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 'bold', color: '#ef4444' }}>
                  Missing Review Links
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Some of your tours are missing Review links. Automated review requests will not be sent for these tours until you fix this.
                </Typography>
                <Box mt={2} display="flex" justifyContent="flex-end">
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      handleAlertClose();
                      history.push('/tipdirect/setup');
                    }}
                    style={{ textTransform: 'none' }}
                  >
                    Go to Setup
                  </Button>
                </Box>
              </Box>
            </Popover>
          </>
        )}
        <AdminJobsButton />
        <div style={{ marginRight: '4px' }}>
          <HelpMenu />
        </div>
        {/* Same gate as the Settings tab and page (card 362): a guide without
            their own subscription would land on an empty page. */}
        {canSeeSettingsTab(user) && (
          <Tooltip title={intl.formatMessage(messages.settings)}>
            <IconButton
              aria-label={intl.formatMessage(messages.settings)}
              size="small"
              onClick={() => history.push('/settings')}
              style={{ marginRight: '4px' }}>
              <SettingsIcon style={{ color: '#565656' }} />
            </IconButton>
          </Tooltip>
        )}
        <SettingsMenu
          aria-controls="simple-menu"
          aria-haspopup="true"
          onClick={handleProfileMenuOpen}
          isUsingEmail={!username}>
          {username || user.email}
        </SettingsMenu>
      </div>
    </Toolbar>
  );
}

export default DesktopNavBar;
